import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axiosInstance from '../utils/axiosInstance';
import {jwtDecode} from 'jwt-decode';

// Create authentication context
const AuthContext = createContext();

// Custom hook to use authentication context
export const useAuth = () => useContext(AuthContext);

// Authentication provider component
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const brandSyncAttemptedRef = useRef(false);
  const initializeAttemptedRef = useRef(false);

  const syncBrandIfNeeded = useCallback(async (userData) => {
    if (!userData?.is_staff) return;
    const storedBrand = localStorage.getItem('selectedBrand');
    const currentBrand = userData?.brand || '';
    if (!storedBrand) {
      if (currentBrand) {
        localStorage.setItem('selectedBrand', currentBrand);
      }
      return;
    }
    if (brandSyncAttemptedRef.current) return;
    if (storedBrand === currentBrand) return;
    try {
      brandSyncAttemptedRef.current = true;
      const response = await axiosInstance.patch('/api/users/me/brand/', { brand: storedBrand });
      // Update user with response data
      const updatedUser = {
        ...userData,
        brand: response.data.brand,
        allowed_brands: response.data.allowed_brands || userData.allowed_brands
      };
      localStorage.setItem('userDetails', JSON.stringify(updatedUser));
      setUser(updatedUser);
    } catch (error) {
      // Ignore sync errors
    }
  }, []);

  // Logout function to clear user data and tokens
  const logout = useCallback((sendMessage = true, redirect = true) => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('userDetails');
    localStorage.removeItem('selectedBrand');
    brandSyncAttemptedRef.current = false;
    setUser(null);

    // Broadcast logout message to other tabs if needed
    if (sendMessage) {
      const authChannel = new BroadcastChannel('auth');
      authChannel.postMessage({ type: 'logout' });
    }

    // Redirect to login page
    if (redirect) {
      window.location.href = '/login';
    }
  }, []);

  // Token refresh using Clerk session
  const refreshAccessToken = useCallback(async () => {
    try {
      const clerk = window.Clerk;
      if (!clerk || !clerk.session) {
        return null;
      }

      // Get fresh token from Clerk
      const clerkToken = await clerk.session.getToken();
      if (!clerkToken) {
        return null;
      }

      // Exchange for backend token
      const response = await axiosInstance.post('/api/auth/clerk/', {
        clerk_token: clerkToken,
        email: clerk.user?.primaryEmailAddress?.emailAddress,
        clerk_user_id: clerk.user?.id,
        first_name: clerk.user?.firstName,
        last_name: clerk.user?.lastName,
        avatar_url: clerk.user?.imageUrl,
      });

      if (response.data.allowed && response.data.access) {
        localStorage.setItem('accessToken', response.data.access);
        if (response.data.refresh) {
          localStorage.setItem('refreshToken', response.data.refresh);
        }
        if (response.data.user) {
          localStorage.setItem('userDetails', JSON.stringify(response.data.user));
          setUser(response.data.user);
        }
        return response.data.access;
      }

      return null;
    } catch (error) {
      console.error('Token refresh failed:', error);
      return null;
    }
  }, []);

  // Initialize authentication on app startup
  const initializeAuth = useCallback(async () => {
    console.log('[AuthProvider] initializeAuth called, attempted:', initializeAttemptedRef.current);

    // Prevent double initialization (React StrictMode or dependency changes)
    if (initializeAttemptedRef.current) {
      console.log('[AuthProvider] Already initialized, skipping');
      return;
    }
    initializeAttemptedRef.current = true;

    const token = localStorage.getItem('accessToken');
    const userDetails = localStorage.getItem('userDetails');

    console.log('[AuthProvider] Token exists:', !!token);
    console.log('[AuthProvider] UserDetails exists:', !!userDetails);

    try {
      if (token) {
        const decoded = jwtDecode(token);
        const currentTime = Date.now() / 1000;
        const timeUntilExpiry = decoded.exp - currentTime;

        console.log('[AuthProvider] Token expires in:', timeUntilExpiry, 'seconds');

        // Check if token is still valid (or will expire in next 60 seconds)
        if (decoded.exp > currentTime + 60) {
          console.log('[AuthProvider] Token is valid');
          if (userDetails) {
            const parsed = JSON.parse(userDetails);
            console.log('[AuthProvider] Setting user from localStorage:', parsed.email);
            setUser(parsed);
            await syncBrandIfNeeded(parsed);
          } else {
            console.log('[AuthProvider] No userDetails, logging out');
            // No user details, force re-login
            logout(false, false);
          }
        } else {
          console.log('[AuthProvider] Token expired or expiring soon, refreshing...');
          // Token expired or expiring soon, try to refresh silently
          const newToken = await refreshAccessToken();
          if (!newToken) {
            console.log('[AuthProvider] Refresh failed, logging out');
            // Refresh failed, clear without redirect (PrivateRoute will handle)
            logout(false, false);
          } else {
            console.log('[AuthProvider] Token refreshed successfully');
          }
        }
      } else {
        console.log('[AuthProvider] No token found');
      }
    } catch (error) {
      console.error('[AuthProvider] Error during init:', error);
      logout(false, false);
    } finally {
      console.log('[AuthProvider] Setting loading to false');
      setLoading(false);
    }
  }, [logout, refreshAccessToken, syncBrandIfNeeded]);

  // Setup BroadcastChannel for cross-tab authentication sync
  useEffect(() => {
    const authChannel = new BroadcastChannel('auth');

    authChannel.onmessage = (event) => {
      if (event.data.type === 'logout') {
        logout(false, false);
      } else if (event.data.type === 'login') {
        const userDetails = localStorage.getItem('userDetails');
        if (userDetails) {
          setUser(JSON.parse(userDetails));
        }
      }
    };

    return () => {
      authChannel.close();
    };
  }, [logout]);

  // Initialize authentication when component mounts
  useEffect(() => {
    initializeAuth();
  }, [initializeAuth]);

  // Refresh user details on demand (e.g., after updating profile data)
  const refreshUser = useCallback(async () => {
    const userDetails = localStorage.getItem('userDetails');
    if (userDetails) {
      setUser(JSON.parse(userDetails));
    }
  }, []);

  // Change the active brand
  const changeBrand = useCallback(async (newBrand) => {
    try {
      const response = await axiosInstance.patch('/api/users/me/brand/', { brand: newBrand });
      // Update local storage
      localStorage.setItem('selectedBrand', newBrand);
      // Update user state with new brand
      setUser(prev => ({
        ...prev,
        brand: response.data.brand,
        allowed_brands: response.data.allowed_brands || prev?.allowed_brands
      }));
      // Also update userDetails in localStorage
      const userDetails = localStorage.getItem('userDetails');
      if (userDetails) {
        const parsed = JSON.parse(userDetails);
        parsed.brand = response.data.brand;
        if (response.data.allowed_brands) {
          parsed.allowed_brands = response.data.allowed_brands;
        }
        localStorage.setItem('userDetails', JSON.stringify(parsed));
      }
      return { success: true };
    } catch (error) {
      console.error('Error changing brand:', error);
      return { success: false, error: error.response?.data?.error || 'Failed to change brand' };
    }
  }, []);

  console.log('[AuthProvider] Render - loading:', loading, 'user:', user?.email || 'null');

  return (
    <AuthContext.Provider value={{ user, setUser, loading, logout, refreshUser, changeBrand }}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
